import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Brain, Zap, Video, Shield, Clock, CreditCard, ChevronDown, AlertCircle, TrendingUp, BookOpen, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';

const LandingPage = () => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 700], [1, 0]);
const heroScale = useTransform(scrollY, [0, 700], [1, 0.95]);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCTAClick = () => {
    // Fire Facebook Pixel InitiateCheckout event
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: 9.90,
        currency: 'USD'
      });
    }
    window.open('https://pay.hotmart.com/X103648540M?checkoutMode=10&bid=1771872077537&src=fb_ads', '_blank');
  };

  return (
    <div className="landing-page">
      {/* Sticky Header */}
      <motion.header
        className={`sticky-header ${isHeaderVisible ? 'visible' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: isHeaderVisible ? 0 : -100 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
      >
        <div className="header-content">
          <span className="header-logo">El Poder de Manifestar</span>
          <Button 
            className="btn-primary-small"
            onClick={handleCTAClick}
          >
            Comprar Ahora
          </Button>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4" 
            alt="AI Neural Network" 
            className="hero-bg-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <motion.div 
          className="hero-content"
          style={{ scale: heroScale }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-badge"
          >
            <Zap className="badge-icon" />
            <span className="text-lg md:text-xl font-bold tracking-wide">Tecnología Innovadora de Reprogramación Mental</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hero-title"
          >
            Activa tu Abundancia en 70 Segundos con videos IA personalizados
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="hero-subtitle text-base md:text-lg text-blue-100/90 mb-6"
          >            Mira este video y descubre cómo el método de los 70 segundos está transformando miles de realidades ahora mismo.

          </motion.p>
         <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: '100%', background: '#000', margin: '20px 0', borderRadius: '8px' }}>
  <iframe 
    src="https://www.youtube.com/embed/FWNCD-oisF8?si=oI3GjFcMFwXozv0W" 
    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} 
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
    allowFullScreen
  ></iframe>
</div><p className="text-base md:text-lg text-blue-100/80 mb-8 leading-relaxed italic border-l-2 border-blue-500/30 pl-4">
  Basado en ciencia aplicada, nuestro método utiliza un entrenamiento de visualización personalizada en sesiones estratégicas de solo 70 segundos (al despertar y antes de dormir). Al completar este protocolo durante 21 días consecutivos, logras una reprogramación mental profunda y efectiva, consolidando nuevos patrones neuronales que transforman tu realidad de forma permanente.
</p>

<motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.8 }}
  className="hero-cta-container"
          >
            <Button 
              className="btn-primary-hero"
              onClick={handleCTAClick}
            >
              Acceder al Método por solo $9.90 USD
            </Button>
  {/* Bloque de Confianza: Pago Único */}
<div style={{
    maxWidth: '340px', 
    margin: '10px auto 20px', 
    padding: '12px', 
    border: '1px solid #e0e0e0', 
    borderRadius: '10px', 
    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
    backdropFilter: 'blur(5px)',
    textAlign: 'center', 
    fontFamily: 'sans-serif'
}}>
    <div style={{ color: '#00ff88', fontWeight: 'bold', fontSize: '14px', marginBottom: '5px' }}>
        ✅ PAGO ÚNICO - ACCESO DE POR VIDA
    </div>
    <div style={{ fontSize: '13px', color: '#fff', opacity: 0.9, lineHeight: '1.4' }}>
        Sin suscripciones ni cuotas mensuales. <br />
        Solo pagas <strong>$9.90 USD</strong> una vez.
    </div>
    <div style={{ marginTop: '8px', fontSize: '12px', color: '#aaa' }}>
        🔒 Transacción segura vía <strong>Hotmart</strong>
    </div>
</div>
            <p className="hero-cta-subtext">✓ Acceso inmediato • ✓ Garantía de 7 días</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="scroll-indicator"
            onClick={scrollToOffer}
          >
            <ChevronDown className="scroll-icon" />
          </motion.div>
        </motion.div>
      </section>

      {/* Problem/Pain Section - NUEVO */}
      <section className="problem-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="problem-content"
          >
            <div className="problem-icon-wrapper">
              <AlertCircle className="problem-icon" />
            </div>
            <h2 className="problem-title">¿Te Sientes Atrapado en Patrones Mentales Limitantes?</h2>
            <div className="problem-text-container">
              <p className="problem-text">
                Desde la infancia, tu subconsciente ha grabado programas, memorias y hábitos que hoy 
                te limitan. Situaciones de estrés, angustia, temor y procrastinación se repiten una y 
                otra vez, generando neuropéptidos que mantienen tu cuerpo en un estado de sufrimiento constante.
              </p>
              <p className="problem-text">
                Tu cerebro no distingue entre una situación imaginaria y una real. Cada vez que revives 
                mentalmente experiencias negativas, tu cuerpo produce las mismas emociones dañinas como si 
                estuvieras viviéndolas de nuevo. Cuando esto se vuelve crónico, creas una vida de limitación 
                y sufrimiento.
              </p>
              <h3 className="text-2xl font-bold text-center mt-10 mb-6">Este podría ser tu caso...</h3>
              <div className="problem-list">
                <div className="problem-item">
                  <div className="problem-check">✗</div>
                  <span>Repites patrones de pensamientos negativos desde hace años</span>
                </div>
                <div className="problem-item">
                  <div className="problem-check">✗</div>
                  <span>Sientes que no puedes avanzar hacia tus objetivos</span>
                </div>
                <div className="problem-item">
                  <div className="problem-check">✗</div>
                  <span>Vives con ansiedad, estrés o procrastinación constante</span>
                </div>
                <div className="problem-item">
                  <div className="problem-check">✗</div>
                  <span>Sabes que mereces más pero no logras cambiar</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Before & After Video Section - NUEVO */}
      <section className="transformation-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="transformation-content"
          >
            <div className="transformation-text">
              <TrendingUp className="transformation-badge-icon" />
              <h2 className="transformation-title">La Transformación es Posible</h2>
              <p className="transformation-description">
                Descubre cómo puedes romper con ese difícil pasado y generar un estado interior 
                totalmente diferente. Mira un avance de alguien que aplicó el método de los 70 segundos con la lectura y enseñanzas contenidas en nuestro Ebook "Mírate viviendo la vida que deseas":
              </p>
            </div>
            
            <div className="transformation-video-wrapper">
              <div className="vertical-video-container">
                <iframe 
                  src="https://www.youtube.com/embed/lJhSUqo23RU"
                  title="Transformación - Antes y Después"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="vertical-video-iframe"
                />
              </div>
            </div>

            <p className="transformation-cta-text">
              Esta puede ser tu historia. Con práctica sostenida y las enseñanzas correctas, 
              tu vida puede cambiar completamente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Visual Science Section */}
      <section className="science-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">La Ciencia Visual Detrás del Método</h2>
            <p className="section-subtitle">
              Tres pilares fundamentales que hacen funcionar la reprogramación con videos IA personalizados
            </p>
          </motion.div>

          <div className="science-grid">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="science-card">
                <CardContent className="science-card-content">
                  <div className="science-icon-wrapper">
                    <Brain className="science-icon" />
                  </div>
                  <h3 className="science-card-title">Neuroplasticidad</h3>
                  <p className="science-card-text">
                    Tu cerebro se reconfigura cuando visualizas con claridad. Los videos hiperrealistas personalizados 
                    crean nuevas conexiones neuronales que transforman tu realidad desde el subconsciente.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="science-card">
                <CardContent className="science-card-content">
                  <div className="science-icon-wrapper">
                    <Zap className="science-icon" />
                  </div>
                  <h3 className="science-card-title">Personalización por IA</h3>
                  <p className="science-card-text">
                    Inteligencia artificial que genera videos únicos basados en tu propia imagen y en tus objetivos particulares. 
                    No hay videos genéricos, cada visualización es tuya y solo tuya.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card className="science-card">
                <CardContent className="science-card-content">
                  <div className="science-icon-wrapper">
                    <Video className="science-icon" />
                  </div>
                  <h3 className="science-card-title">Regla de los 70 Segundos</h3>
                  <p className="science-card-text">
                    El tiempo exacto que tu subconsciente necesita para absorber información sin resistencia. 
                    Sesiones cortas, resultados poderosos y duraderos.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ebook Hero Section - NUEVO */}
      <section className="ebook-hero-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="ebook-hero-content"
          >
            <div className="ebook-hero-badge">
              <BookOpen className="ebook-badge-icon" />
              <span>TU GUÍA COMPLETA DE TRANSFORMACIÓN</span>
            </div>

            <h2 className="ebook-hero-title">
              Ebook: Mírate Viviendo la Vida que Deseas
            </h2>

            <p className="ebook-hero-subtitle">
              Tu guía digital definitivo que te enseña paso a paso cómo crear videos IA personalizados 
              con tu propia imagen, representándote en escenarios hiperrealistas de abundancia, 
              salud y éxito.
            </p>

            <div className="ebook-hero-grid">
              <div className="ebook-hero-image">
                <img 
                  src="https://customer-assets.emergentagent.com/job_reprogram-70s/artifacts/mbpaua6w_Flyer1%20mockup.png"
                  alt="Ebook Mírate Viviendo la Vida que Deseas"
                  className="ebook-mockup"
                />
              </div>

              <div className="ebook-hero-info">
                <h3 className="ebook-info-title">Lo que aprenderás:</h3>
                
                <div className="ebook-feature-list">
                  <div className="ebook-feature">
                    <div className="ebook-feature-icon">🎯</div>
                    <div>
                      <h4>Creación de Videos IA Personalizados</h4>
                      <p>Aprende toda la metodología para generar videos hiperrealistas con tu propia imagen como protagonista de tu éxito</p>
                    </div>
                  </div>

                  <div className="ebook-feature">
                    <div className="ebook-feature-icon">🧠</div>
                    <div>
                      <h4>El Método de los 70 Segundos</h4>
                      <p>Descubre por qué 70 segundos es el tiempo exacto para reprogramar tu subconsciente sin activar resistencia mental</p>
                    </div>
                  </div>

                  <div className="ebook-feature">
                    <div className="ebook-feature-icon">⚡</div>
                    <div>
                      <h4>Neuroplasticidad y Neuropéptidos</h4>
                      <p>Entiende la ciencia: tu cerebro no distingue lo imaginario de lo real y produce neuropéptidos que transforman tu realidad para bien o para mal</p>
                    </div>
                  </div>

                  <div className="ebook-feature">
                    <div className="ebook-feature-icon">🔄</div>
                    <div>
                      <h4>Eliminación de Programas Limitantes</h4>
                      <p>Borra memorias y hábitos negativos grabados desde la infancia que te mantienen atrapado en patrones de sufrimiento</p>
                    </div>
                  </div>

                  <div className="ebook-feature">
                    <div className="ebook-feature-icon">✨</div>
                    <div>
                      <h4>Generación de Nuevos Hábitos</h4>
                      <p>Se ejercita aplicando la visualización de videos de 70 segundos durante 5 minutos consecutivos por día, tanto al despertar como antes de dormir y a lo largo de un entrenamiento de 21 días ininterrumpidos para conseguir una efectiva reprogramación.</p>
                    </div>
                  </div>
                </div>

                <div className="ebook-hero-cta">
                  <Button 
                    className="btn-primary-ebook"
                    onClick={handleCTAClick}
                  >
                    Obtener el Ebook por $9.90 USD
                  </Button>
                  {/* Segundo Remache de Confianza */}
<div style={{
    maxWidth: '340px', 
    margin: '15px auto', 
    padding: '12px', 
    border: '1px solid #e0e0e0', 
    borderRadius: '10px', 
    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
    backdropFilter: 'blur(5px)',
    textAlign: 'center', 
    fontFamily: 'sans-serif'
}}>
    <div style={{ color: '#00ff88', fontWeight: 'bold', fontSize: '14px', marginBottom: '5px' }}>
        ✅ PAGO ÚNICO - SIN MENSUALIDADES
    </div>
    <div style={{ fontSize: '13px', color: '#fff', opacity: 0.9, lineHeight: '1.4' }}>
        Acceso de por vida por solo <strong>$9.90 USD</strong>. <br />
        No hay cobros recurrentes ni sorpresas.
    </div>
    <div style={{ marginTop: '8px', fontSize: '12px', color: '#aaa' }}>
        🔒 Compra 100% Protegida
    </div>
</div>
                  <p className="ebook-cta-note">Incluye todo el método + Bono 50 Prompts IA + Entrenamiento 21 Días</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="video-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="video-content"
          >
            <div className="video-text">
              <h2 className="video-title">Mira Cómo Funciona</h2>
              <p className="video-description">
                Descubre en este video cómo la IA genera visualizaciones personalizadas 
                que reprograman tu mente para el éxito en solo 70 segundos al día.
              </p>
              <div className="video-features">
                <div className="video-feature">
                  <div className="feature-check">✓</div>
                  <span>Videos 100% personalizados</span>
                </div>
                <div className="video-feature">
                  <div className="feature-check">✓</div>
                  <span>Basado en neurociencia</span>
                </div>
                <div className="video-feature">
                  <div className="feature-check">✓</div>
                  <span>Resultados medibles</span>
                </div>
              </div>
            </div>
            <div className="video-player-wrapper">
              <iframe 
                src="https://www.youtube.com/embed/v6zkhOKtLMM"
                title="Video explicativo - El Poder de Manifestar"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Offer Stack Section */}
      <section className="offer-section" id="offer-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Accede Hoy al Método Completo</h2>
            <p className="section-subtitle">
              Todo lo que necesitas para reprogramar tu subconsciente y manifestar tu realidad ideal
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="offer-card">
              <CardContent className="offer-card-content">
                <div className="offer-badge">OFERTA ESPECIAL</div>
                
                {/* Urgency Message - Static */}
                <div className="urgency-message mt-4 mb-2">
                 <p className="text-gray-400 text-sm font-medium">
  🔥 Oferta Especial de Lanzamiento - Tiempo Limitado
</p>
                </div>
                
                <div className="offer-items">
                  <div className="offer-item">
                    <div className="offer-item-check">✓</div>
                    <div className="offer-item-content">
                      <h4>Ebook: Mírate viviendo la vida que deseas</h4>
                      <p>Guía completa del método con fundamentos científicos</p>
                      <span className="offer-item-value">Valor: $37 USD</span>
                    </div>
                  </div>

                  <div className="offer-item">
                    <div className="offer-item-check">✓</div>
                    <div className="offer-item-content">
                      <h4>Oráculo con 50 PROMPTS IA</h4>
                      <p>Recibes 50 prompts especiales que permiten mejorar diferentes áreas de tu vida generando videos debidamente personalizados</p>
                      <span className="offer-item-value">Valor: $27 USD</span>
                    </div>
                  </div>

                  <div className="offer-item">
                    <div className="offer-item-check">✓</div>
                    <div className="offer-item-content">
                      <h4>Entrenamiento 21 Días</h4>
                      <p>Programa estructurado para formar el hábito de generar nuevos pensamientos, emociones y acciones para tu vida diaria</p>
                      <span className="offer-item-value">Valor: $33 USD</span>
                    </div>
                  </div>
                </div>

                <div className="offer-divider"></div>

                <div className="offer-pricing">
                  <div className="offer-total">
                    <span className="offer-total-label">Valor Total:</span>
                    <span className="offer-total-value">$97 USD</span>
                  </div>
                  <div className="offer-today">
                    <span className="offer-today-label">Precio Hoy:</span>
                    <span className="offer-today-value">$9.90 USD</span>
                  </div>
                  <div className="offer-savings">
                    <span>Ahorras: $87.10 USD (90% de descuento)</span>
                  </div>
                </div>

                <Button 
                  className="btn-primary-offer"
                  onClick={handleCTAClick}
                >
                  Acceder al Método por $9.90 USD
                </Button>
                {/* Refuerzo de Confianza: Pago Único */}
<div style={{
    maxWidth: '340px', 
    margin: '15px auto', 
    padding: '12px', 
    border: '1px solid rgba(255, 255, 255, 0.2)', 
    borderRadius: '10px', 
    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
    backdropFilter: 'blur(5px)',
    textAlign: 'center', 
    fontFamily: 'sans-serif'
}}>
    <div style={{ color: '#00ff88', fontWeight: 'bold', fontSize: '14px', marginBottom: '5px' }}>
        ✅ PAGO ÚNICO - SIN MENSUALIDADES
    </div>
    <div style={{ fontSize: '13px', color: '#fff', opacity: 0.9, lineHeight: '1.4' }}>
        Acceso de por vida por solo <strong>$9.90 USD</strong>. <br />
        No hay cobros recurrentes ni sorpresas.
    </div>
    <div style={{ marginTop: '8px', fontSize: '12px', color: '#aaa' }}>
        🔒 Compra 100% Protegida por Hotmart
    </div>
</div>

                <div className="offer-guarantee">
                  <Shield className="guarantee-icon" />
                  <p>Garantía total de 7 días. Si no ves resultados, te devolvemos tu dinero sin preguntas.</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-grid">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="trust-item"
            >
              <CreditCard className="trust-icon" />
              <div>
                <h4 className="trust-title">Pago Seguro</h4>
                <p className="trust-text">Procesado vía Hotmart</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="trust-item"
            >
              <Shield className="trust-icon" />
              <div>
                <h4 className="trust-title">7 Días de Garantía</h4>
                <p className="trust-text">100% de devolución</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="trust-item"
            >
              <Clock className="trust-icon" />
              <div>
                <h4 className="trust-title">Acceso Inmediato</h4>
                <p className="trust-text">Tras completar la compra</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials Section - NUEVO */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Lo que dicen nuestros usuarios</h2>
            <p className="section-subtitle">
              Personas reales, transformaciones reales
            </p>
          </motion.div>

          <div className="testimonials-grid">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="testimonial-stars">
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                  </div>
                  <p className="testimonial-text">
                    "Realmente funciona aplicando 70 segundos de visualización por sesión y durante 21 días seguidos, ¡Muy recomendado!"
                  </p>
                  <div className="testimonial-author">
                    <div className="author-avatar">L</div>
                    <div>
                      <p className="author-name">Lorena</p>
                      <p className="author-location">México</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="testimonial-stars">
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                  </div>
                  <p className="testimonial-text">
                    "La integración de IA y neurociencia en este método es impresionante. 
                    Simple pero poderoso."
                  </p>
                  <div className="testimonial-author">
                    <div className="author-avatar">M</div>
                    <div>
                      <p className="author-name">Marco</p>
                      <p className="author-location">España</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="testimonial-stars">
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                    <Star className="star-icon filled" />
                  </div>
                  <p className="testimonial-text">
                    "Los mejores $9.90 que he gastado en mi vida. La evidencia visual hace que 
                    manifestar resultados se sienta sin esfuerzo."
                  </p>
                  <div className="testimonial-author">
                    <div className="author-avatar">S</div>
                    <div>
                      <p className="author-name">Sofía</p>
                      <p className="author-location">Argentina</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Preguntas Frecuentes</h2>
            <p className="section-subtitle">
              Todo lo que necesitas saber acerca de la reprogramacion mental mediante videos IA personalizados
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="faq-container"
          >
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1" className="faq-item">
                <AccordionTrigger className="faq-trigger">
                  ¿Cómo funciona exactamente la IA para crear videos personalizados?
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  La inteligencia artificial utiliza tus objetivos específicos, valores y aspiraciones para 
                  generar visualizaciones únicas. Con los 50 prompts incluidos, guías a la IA para crear 
                  videos que representen exactamente tu versión de éxito, no una plantilla genérica.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="faq-item">
                <AccordionTrigger className="faq-trigger">
                  ¿Cuál es la base científica del método?
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  El método se basa en neuroplasticidad y visualización dirigida. Estudios demuestran que 
                  el cerebro no distingue entre experiencias reales e imaginadas vívidamente. Al ver videos 
                  personalizados de tu éxito durante 70 segundos, creas nuevas vías neuronales que impulsan 
                  comportamientos alineados con tus objetivos.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="faq-item">
                <AccordionTrigger className="faq-trigger">
                  ¿Por qué 70 segundos específicamente?
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  Los 70 segundos son el punto óptimo donde tu subconsciente absorbe información sin activar 
                  resistencia mental. Es lo suficientemente largo para crear impacto, pero lo suficientemente 
                  corto para mantener tu mente en estado receptivo sin distracciones o juicios críticos.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="faq-item">
                <AccordionTrigger className="faq-trigger">
                  ¿Necesito experiencia con IA o tecnología avanzada?
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  No necesitas ninguna experiencia técnica. El Bono 2, Oráculo de 50 Prompts, te proporciona prompts 
                  listos para usar. Solo completas con tu información personal y la IA hace el resto. Es tan 
                  simple como copiar, pegar y personalizar.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="faq-item">
                <AccordionTrigger className="faq-trigger">
                  ¿Cuánto tiempo hasta ver resultados?
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  Los cambios en tu mentalidad pueden notarse en los primeros 7 días. El entrenamiento de 21 
                  días está diseñado para crear un hábito sólido. Los resultados externos varían según tus 
                  objetivos, pero la mayoría reporta cambios significativos en su forma de pensar y actuar 
                  dentro de la primera semana.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="faq-item">
                <AccordionTrigger className="faq-trigger">
                  ¿Qué pasa si no funciona para mí?
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  Tienes 7 días completos para probar el método sin riesgo. Si por cualquier razón decides que 
                  no es para ti, simplemente contacta a soporte y recibes un reembolso total, sin preguntas. 
                  Tu satisfacción está garantizada.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="final-cta-content"
          >
            <h2 className="final-cta-title">
              Comienza la Transformación de tu Vida Hoy
            </h2>
            <p className="final-cta-text">
              90% de descuento disponible solo por tiempo limitado
            </p>
            <Button 
              className="btn-primary-final"
              onClick={handleCTAClick}
            >
              Acceder Ahora por $9.90 USD
            </Button>
            {/* Último Refuerzo de Confianza: Pago Único */}
<div style={{
    maxWidth: '340px', 
    margin: '15px auto', 
    padding: '12px', 
    border: '1px solid rgba(255, 255, 255, 0.2)', 
    borderRadius: '10px', 
    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
    backdropFilter: 'blur(5px)',
    textAlign: 'center', 
    fontFamily: 'sans-serif'
}}>
    <div style={{ color: '#00ff88', fontWeight: 'bold', fontSize: '14px', marginBottom: '5px' }}>
        ✅ PAGO ÚNICO - ACCESO DE POR VIDA
    </div>
    <div style={{ fontSize: '13px', color: '#fff', opacity: 0.9, lineHeight: '1.4' }}>
        Sin suscripciones ni cobros mensuales. <br />
        Solo pagas <strong>$9.90 USD</strong> una vez.
    </div>
    <div style={{ marginTop: '8px', fontSize: '12px', color: '#aaa' }}>
        🔒 Compra 100% Protegida por Hotmart
    </div>
</div>
            <p className="final-cta-subtext">
              🔒 Pago seguro • ✓ Garantía 7 días • ⚡ Acceso inmediato
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="footer-text">
            Desarrollado por <strong>profesor Albert Ubba</strong>: Investigador visionario en el desarrollo del potencial humano, 
            emprendedor, líder en la industria minera y mentor en tecnologías disruptivas.
          </p>
          <p className="footer-text">
            © 2026 El Poder de Manifestar. Todos los derechos reservados.
          </p>
          <p className="footer-subtext">
            Este producto es comercializado con el soporte de Hotmart. La plataforma no hace 
            revisión previa de todos los productos vendidos, ni evalúa la tecnicidad o calidad.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
